import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, SwapActionCreators } from "@nkr/store/index";
import { ILogisticsResponse, ILogistics } from "@nkr/models/index";
import { PhoneType, TransportMethod } from '@nkr/enums/index';

interface IProps {
    donorId: number,
    recipientId: number
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getLogisticsData: (donorId: number, recipientId: number) => Promise<ILogisticsResponse>
}

type TProps = IStateProps & IDispatchProps & IProps;

interface IState {
    logistics: ILogistics
}

class LogisticsPlan extends React.Component<TProps, IState> {
    readonly state: IState = {
        logistics: null
    }

    componentDidMount = () => {
        this.props.showLoading('Retrieving plan information, one moment please...').then(() => {
            this.props.getLogisticsData(this.props.donorId, this.props.recipientId).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    this.setState({
                        logistics: response.data.results
                    });
                }
            });
        });
    }

    generateArrays = (): { primaryHeaders: string[], primaryValues: string[], backupHeaders: string[], backupValues: string[] } => {
        const {
            donorCenterTimezone, recipientCenterTimezone,
            isRemoteDonation, rdnCenterTimezone,
            transportMethod, transportMethodBackup,
            transportCost, transportCostBackup,
            pickupGroundMiles, pickupGroundMilesBackup,
            flightRouteOrGround, flightRouteOrGroundBackup,
            flightCarrierAndNumber, flightCarrierAndNumberBackup,
            flightDepartureTime, flightDepartureTimeTz, flightDepartureTimeBackup, flightDepartureTimeBackupTz,
            flightArrivalTime, flightArrivalTimeTz, flightArrivalTimeBackup, flightArrivalTimeBackupTz,
            leg2DepartureTime, leg2DepartureTimeTz, leg2DepartureTimeBackup, leg2DepartureTimeBackupTz,
            leg2ArrivalTime, leg2ArrivalTimeTz, leg2ArrivalTimeBackup, leg2ArrivalTimeBackupTz,
            dropoffGroundMiles, dropoffGroundMilesBackup } = this.state.logistics;

        const donorCenterTz = !isRemoteDonation ? donorCenterTimezone : rdnCenterTimezone;

        let primaryHeaders: string[] = [];
        let primaryValues: string[] = [];

        let backupHeaders: string[] = [];
        let backupValues: string[] = [];

        // Ground
        if (parseInt(transportMethod) == TransportMethod.Ground) {
            primaryHeaders.push('Ground Miles');
            primaryValues.push(dropoffGroundMiles);
        }

        // Ground - Backup
        if (parseInt(transportMethodBackup) == TransportMethod.Ground) {
            backupHeaders.push('Ground Miles');
            backupValues.push(dropoffGroundMilesBackup);
        }

        // Commercial or CommOngLegObc
        if (parseInt(transportMethod) == TransportMethod.Commercial || parseInt(transportMethod) == TransportMethod.CommOneLegObc) {
            let flightDepartureTimezone = flightDepartureTimeTz ? flightDepartureTimeTz : donorCenterTz;
            let flightArrivalTimezone = flightArrivalTimeTz ? flightArrivalTimeTz : recipientCenterTimezone;

            primaryHeaders.push('Pickup Up Ground Miles');
            primaryValues.push(pickupGroundMiles);

            primaryHeaders.push('Flight Route');
            primaryValues.push(flightRouteOrGround);

            primaryHeaders.push('Flight #');
            primaryValues.push(flightCarrierAndNumber);

            primaryHeaders.push('Take-Off Time');
            primaryValues.push(flightDepartureTime + ' ' + flightDepartureTimezone);

            primaryHeaders.push('Landing Time');
            primaryValues.push(flightArrivalTime + ' ' + flightArrivalTimezone);

            primaryHeaders.push('Drop Off Ground Miles');
            primaryValues.push(dropoffGroundMiles);
        }

        // Commercial or CommOngLegObc - Backup
        if (parseInt(transportMethodBackup) == TransportMethod.Commercial || parseInt(transportMethodBackup) == TransportMethod.CommOneLegObc) {
            let flightDepartureTimezone = flightDepartureTimeBackupTz ? flightDepartureTimeBackupTz : donorCenterTz;
            let flightArrivalTimezone = flightArrivalTimeBackupTz ? flightArrivalTimeBackupTz : recipientCenterTimezone;

            backupHeaders.push('Pickup Up Ground Miles');
            backupValues.push(pickupGroundMilesBackup);

            backupHeaders.push('Flight Route');
            backupValues.push(flightRouteOrGroundBackup);

            backupHeaders.push('Flight #');
            backupValues.push(flightCarrierAndNumberBackup);

            backupHeaders.push('Take-Off Time');
            backupValues.push(flightDepartureTimeBackup + ' ' + flightDepartureTimezone);

            backupHeaders.push('Landing Time');
            backupValues.push(flightArrivalTimeBackup + ' ' + flightArrivalTimezone);

            backupHeaders.push('Drop Off Ground Miles');
            backupValues.push(dropoffGroundMilesBackup);
        }

        // CommObc
        if (parseInt(transportMethod) == TransportMethod.CommObc) {
            let flightDepartureTimezone = flightDepartureTimeTz ? flightDepartureTimeTz : donorCenterTz;
            let flightArrivalTimezone = flightArrivalTimeTz ? flightArrivalTimeTz : recipientCenterTimezone;
            let leg2DepartureTimezone = leg2DepartureTimeTz ? leg2DepartureTimeTz : donorCenterTz;
            let leg2ArrivalTimezone = leg2ArrivalTimeTz ? leg2ArrivalTimeTz : recipientCenterTimezone;

            primaryHeaders.push('Pickup Up Ground Miles');
            primaryValues.push(pickupGroundMiles);

            primaryHeaders.push('Flight Route');
            primaryValues.push(flightRouteOrGround);

            primaryHeaders.push('Flight #');
            primaryValues.push(flightCarrierAndNumber);

            primaryHeaders.push('1st Flight Take-Off Time');
            primaryValues.push(flightDepartureTime + ' ' + flightDepartureTimezone);

            primaryHeaders.push('1st Flight Landing Time');
            primaryValues.push(flightArrivalTime + ' ' + flightArrivalTimezone);

            primaryHeaders.push('2nd Flight Take-Off Time');
            primaryValues.push(leg2DepartureTime + ' ' + leg2DepartureTimezone);

            primaryHeaders.push('2nd Flight Landing Time');
            primaryValues.push(leg2ArrivalTime + ' ' + leg2ArrivalTimezone);

            primaryHeaders.push('Drop Off Ground Miles');
            primaryValues.push(dropoffGroundMiles);
        }

        // CommObc - Backup
        if (parseInt(transportMethodBackup) == TransportMethod.CommObc) {
            let flightDepartureTimezone = flightDepartureTimeBackupTz ? flightDepartureTimeBackupTz : donorCenterTz;
            let flightArrivalTimezone = flightArrivalTimeBackupTz ? flightArrivalTimeBackupTz : recipientCenterTimezone;
            let leg2DepartureTimezone = leg2DepartureTimeBackupTz ? leg2DepartureTimeBackupTz : donorCenterTz;
            let leg2ArrivalTimezone = leg2ArrivalTimeBackupTz ? leg2ArrivalTimeBackupTz : recipientCenterTimezone;

            backupHeaders.push('Pickup Up Ground Miles');
            backupValues.push(pickupGroundMilesBackup);

            backupHeaders.push('Flight Route');
            backupValues.push(flightRouteOrGroundBackup);

            backupHeaders.push('Flight #');
            backupValues.push(flightCarrierAndNumberBackup);

            backupHeaders.push('1st Flight Take-Off Time');
            backupValues.push(flightDepartureTimeBackup + ' ' + flightDepartureTimezone);

            backupHeaders.push('1st Flight Landing Time');
            backupValues.push(flightArrivalTimeBackup + ' ' + flightArrivalTimezone);

            backupHeaders.push('2nd Flight Take-Off Time');
            backupValues.push(leg2DepartureTimeBackup + ' ' + leg2DepartureTimezone);

            backupHeaders.push('2nd Flight Landing Time');
            backupValues.push(leg2ArrivalTimeBackup + ' ' + leg2ArrivalTimezone);

            backupHeaders.push('Drop Off Ground Miles');
            backupValues.push(dropoffGroundMilesBackup);
        }

        // CommCharter
        if (parseInt(transportMethod) == TransportMethod.CommCharter) {
            let flightDepartureTimezone = flightDepartureTimeTz ? flightDepartureTimeTz : donorCenterTz;
            let flightArrivalTimezone = flightArrivalTimeTz ? flightArrivalTimeTz : recipientCenterTimezone;
            let leg2DepartureTimezone = leg2DepartureTimeTz ? leg2DepartureTimeTz : donorCenterTz;
            let leg2ArrivalTimezone = leg2ArrivalTimeTz ? leg2ArrivalTimeTz : recipientCenterTimezone;

            primaryHeaders.push('Transportation Cost')
            primaryValues.push(transportCost);

            primaryHeaders.push('Pickup Up Ground Miles');
            primaryValues.push(pickupGroundMiles);

            primaryHeaders.push('Flight Route');
            primaryValues.push(flightRouteOrGround);

            primaryHeaders.push('Flight #');
            primaryValues.push(flightCarrierAndNumber);

            primaryHeaders.push('Commercial Flight Take-Off Time');
            primaryValues.push(flightDepartureTime + ' ' + flightDepartureTimezone);

            primaryHeaders.push('Commercial Flight Landing Time');
            primaryValues.push(flightArrivalTime + ' ' + flightArrivalTimezone);

            primaryHeaders.push('Charter Flight Take-Off Time');
            primaryValues.push(leg2DepartureTime + ' ' + leg2DepartureTimezone);

            primaryHeaders.push('Charter Flight Landing Time');
            primaryValues.push(leg2ArrivalTime + ' ' + leg2ArrivalTimezone);

            primaryHeaders.push('Drop Off Ground Miles');
            primaryValues.push(dropoffGroundMiles);
        }

        // CommCharter - Backup
        if (parseInt(transportMethodBackup) == TransportMethod.CommCharter) {
            let flightDepartureTimezone = flightDepartureTimeBackupTz ? flightDepartureTimeBackupTz : donorCenterTz;
            let flightArrivalTimezone = flightArrivalTimeBackupTz ? flightArrivalTimeBackupTz : recipientCenterTimezone;
            let leg2DepartureTimezone = leg2DepartureTimeBackupTz ? leg2DepartureTimeBackupTz : donorCenterTz;
            let leg2ArrivalTimezone = leg2ArrivalTimeBackupTz ? leg2ArrivalTimeBackupTz : recipientCenterTimezone;

            backupHeaders.push('Transportation Cost')
            backupValues.push(transportCostBackup);

            backupHeaders.push('Pickup Up Ground Miles');
            backupValues.push(pickupGroundMilesBackup);

            backupHeaders.push('Flight Route');
            backupValues.push(flightRouteOrGroundBackup);

            backupHeaders.push('Flight #');
            backupValues.push(flightCarrierAndNumberBackup);

            backupHeaders.push('Commercial Flight Take-Off Time');
            backupValues.push(flightDepartureTimeBackup + ' ' + flightDepartureTimezone);

            backupHeaders.push('Commercial Flight Landing Time');
            backupValues.push(flightArrivalTimeBackup + ' ' + flightArrivalTimezone);

            backupHeaders.push('Charter Flight Take-Off Time');
            backupValues.push(leg2DepartureTimeBackup + ' ' + leg2DepartureTimezone);

            backupHeaders.push('Charter Flight Landing Time');
            backupValues.push(leg2ArrivalTimeBackup + ' ' + leg2ArrivalTimezone);

            backupHeaders.push('Drop Off Ground Miles');
            backupValues.push(dropoffGroundMilesBackup);
        }

        // CharterComm
        if (parseInt(transportMethod) == TransportMethod.CharterComm) {
            let flightDepartureTimezone = flightDepartureTimeTz ? flightDepartureTimeTz : donorCenterTz;
            let flightArrivalTimezone = flightArrivalTimeTz ? flightArrivalTimeTz : recipientCenterTimezone;
            let leg2DepartureTimezone = leg2DepartureTimeTz ? leg2DepartureTimeTz : donorCenterTz;
            let leg2ArrivalTimezone = leg2ArrivalTimeTz ? leg2ArrivalTimeTz : recipientCenterTimezone;

            primaryHeaders.push('Transportation Cost')
            primaryValues.push(transportCost);

            primaryHeaders.push('Pickup Up Ground Miles');
            primaryValues.push(pickupGroundMiles);

            primaryHeaders.push('Flight Route');
            primaryValues.push(flightRouteOrGround);

            primaryHeaders.push('Flight #');
            primaryValues.push(flightCarrierAndNumber);

            primaryHeaders.push('Charter Flight Take-Off Time');
            primaryValues.push(flightDepartureTime + ' ' + flightDepartureTimezone);

            primaryHeaders.push('Charter Flight Landing Time');
            primaryValues.push(flightArrivalTime + ' ' + flightArrivalTimezone);

            primaryHeaders.push('Commercial Flight Take-Off Time');
            primaryValues.push(leg2DepartureTime + ' ' + leg2DepartureTimezone);

            primaryHeaders.push('Commercial Flight Landing Time');
            primaryValues.push(leg2ArrivalTime + ' ' + leg2ArrivalTimezone);

            primaryHeaders.push('Drop Off Ground Miles');
            primaryValues.push(dropoffGroundMiles);
        }

        // CharterComm - Backup
        if (parseInt(transportMethodBackup) == TransportMethod.CharterComm) {
            let flightDepartureTimezone = flightDepartureTimeBackupTz ? flightDepartureTimeBackupTz : donorCenterTz;
            let flightArrivalTimezone = flightArrivalTimeBackupTz ? flightArrivalTimeBackupTz : recipientCenterTimezone;
            let leg2DepartureTimezone = leg2DepartureTimeBackupTz ? leg2DepartureTimeBackupTz : donorCenterTz;
            let leg2ArrivalTimezone = leg2ArrivalTimeBackupTz ? leg2ArrivalTimeBackupTz : recipientCenterTimezone;

            backupHeaders.push('Transportation Cost')
            backupValues.push(transportCostBackup);

            backupHeaders.push('Pickup Up Ground Miles');
            backupValues.push(pickupGroundMilesBackup);

            backupHeaders.push('Flight Route');
            backupValues.push(flightRouteOrGroundBackup);

            backupHeaders.push('Flight #');
            backupValues.push(flightCarrierAndNumberBackup);

            backupHeaders.push('Charter Flight Take-Off Time');
            backupValues.push(flightDepartureTimeBackup + ' ' + flightDepartureTimezone);

            backupHeaders.push('Charter Flight Landing Time');
            backupValues.push(flightArrivalTimeBackup + ' ' + flightArrivalTimezone);

            backupHeaders.push('Commercial Flight Take-Off Time');
            backupValues.push(leg2DepartureTimeBackup + ' ' + leg2DepartureTimezone);

            backupHeaders.push('Commercial Flight Landing Time');
            backupValues.push(leg2ArrivalTimeBackup + ' ' + leg2ArrivalTimezone);

            backupHeaders.push('Drop Off Ground Miles');
            backupValues.push(dropoffGroundMilesBackup);
        }

        // CharterComm
        if (parseInt(transportMethod) == TransportMethod.Charter) {
            let flightDepartureTimezone = flightDepartureTimeTz ? flightDepartureTimeTz : donorCenterTz;
            let flightArrivalTimezone = flightArrivalTimeTz ? flightArrivalTimeTz : recipientCenterTimezone;

            primaryHeaders.push('Transportation Cost')
            primaryValues.push(transportCost);

            primaryHeaders.push('Pickup Up Ground Miles');
            primaryValues.push(pickupGroundMiles);

            primaryHeaders.push('Flight Route');
            primaryValues.push(flightRouteOrGround);

            primaryHeaders.push('Flight #');
            primaryValues.push(flightCarrierAndNumber);

            primaryHeaders.push('Charter Flight Take-Off Time');
            primaryValues.push(flightDepartureTime + ' ' + flightDepartureTimezone);

            primaryHeaders.push('Charter Flight Landing Time');
            primaryValues.push(flightArrivalTime + ' ' + flightArrivalTimezone);

            primaryHeaders.push('Drop Off Ground Miles');
            primaryValues.push(dropoffGroundMiles);
        }

        // CharterComm - Backup
        if (parseInt(transportMethodBackup) == TransportMethod.Charter) {
            let flightDepartureTimezone = flightDepartureTimeBackupTz ? flightDepartureTimeBackupTz : donorCenterTz;
            let flightArrivalTimezone = flightArrivalTimeBackupTz ? flightArrivalTimeBackupTz : recipientCenterTimezone;

            backupHeaders.push('Transportation Cost')
            backupValues.push(transportCostBackup);

            backupHeaders.push('Pickup Up Ground Miles');
            backupValues.push(pickupGroundMilesBackup);

            backupHeaders.push('Flight Route');
            backupValues.push(flightRouteOrGroundBackup);

            backupHeaders.push('Flight #');
            backupValues.push(flightCarrierAndNumberBackup);

            backupHeaders.push('Charter Flight Take-Off Time');
            backupValues.push(flightDepartureTimeBackup + ' ' + flightDepartureTimezone);

            backupHeaders.push('Charter Flight Landing Time');
            backupValues.push(flightArrivalTimeBackup + ' ' + flightArrivalTimezone);

            backupHeaders.push('Drop Off Ground Miles');
            backupValues.push(dropoffGroundMilesBackup);
        }

        return {
            primaryHeaders, primaryValues,
            backupHeaders, backupValues
        };
    }

    getWhichDonorKidney = (choice: string) => {
        let validLeft = ['Left', 'L-opt']
        let validRight = ['Right', 'R-opt']

        if (validLeft.includes(choice)) {
            return 'Left';
        }

        if (validRight.includes(choice)) {
            return 'Right';
        }

        return 'Unknown';
    }

    render = () => {
        if (this.state.logistics == null)
            return null;

        const { chainId, clusterNumber, position, swapManager,
            logisticsProviderName, logisticsProviderPhone, jobNumber,
            donorAlias, donorUnosId, donorCenter, donorCenterTimezone,
            donorCenterAddress,
            recipientAlias, recipientCenter, recipientCenterTimezone,
            recipientCenterAddress,
            isRemoteDonation, rdnCenterAlias, rdnCenterTimezone,
            rdnCenterAddress,
            donorCoordinator, recipientCoordinator,
            donorSurgeon, recipientSurgeon,
            gpsNumber, gpsLink,
            kidneyPickupDateTime, kidneyPickupDateTimeBackup, kidneyArrivalDateTime, kidneyArrivalDateTimeBackup,
            transportMethod, transportMethodBackup, tubes, whichDonorKidney } = this.state.logistics;

        const donorCenterAlias = !isRemoteDonation ? donorCenter : rdnCenterAlias;
        const donorCenterTz = !isRemoteDonation ? donorCenterTimezone : rdnCenterTimezone;

        const arrays = this.generateArrays();
        const max = Math.max(arrays.backupHeaders.length, arrays.primaryHeaders.length);
        const rows = _.range(0, max);

        const whichKidney = this.getWhichDonorKidney(whichDonorKidney)

        return (
            <>
                <table className='table table-condensed table-bordered table-striped'>
                    <colgroup>
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '35%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '35%' }} />
                    </colgroup>
                    <tbody>
                        <tr>
                            <th colSpan={4} style={{ fontSize: 20, textAlign: 'center', backgroundColor: 'lightgrey' }}>
                                Logistics Plan for Swap {chainId} / {clusterNumber} / {position} <a target="_blank" href="../Plan.php?cluster=2686"></a>
                            </th>
                        </tr>
                        <tr>
                            <th colSpan={4} style={{ fontSize: 20, textAlign: 'center', backgroundColor: 'lightgrey' }}>Swap Manager: {swapManager}</th>
                        </tr>
                        <tr>
                            <th>Courier Name</th>
                            <td>
                                {logisticsProviderName ?
                                    <>{logisticsProviderName}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{logisticsProviderPhone}</>
                                    :
                                    <>[pending]</>
                                }
                            </td>
                            <th>Courier Job #</th>
                            <td>{jobNumber ? jobNumber : '[pending]'}</td>
                        </tr>
                        <tr>
                            <th>Donor Center</th>
                            <td>
                                {donorCenterAlias}<br />

                                {!isRemoteDonation ?
                                    <>
                                        {donorCenterAddress.addressLine1}<br />
                                        {donorCenterAddress.addressLine2 != '' ? <>{donorCenterAddress.addressLine2}<br /></> : null}
                                        {donorCenterAddress.addressLine3 != '' ? <>{donorCenterAddress.addressLine3}<br /></> : null}
                                        {donorCenterAddress.city}, {donorCenterAddress.stateAbbreviation} {donorCenterAddress.postalCode}
                                    </>
                                    :
                                    <>
                                        {rdnCenterAddress.addressLine1}<br />
                                        {rdnCenterAddress.addressLine2 != '' ? <>{rdnCenterAddress.addressLine2}<br /></> : null}
                                        {rdnCenterAddress.addressLine3 != '' ? <>{rdnCenterAddress.addressLine3}<br /></> : null}
                                        {rdnCenterAddress.city}, {rdnCenterAddress.stateAbbreviation} {rdnCenterAddress.postalCode}
                                    </>
                                }
                            </td>
                            <th>Recipient Center</th>
                            <td>
                                {recipientCenter}<br />

                                {recipientCenterAddress.addressLine1}<br />
                                {recipientCenterAddress.addressLine2 != '' ? <>{recipientCenterAddress.addressLine2}<br /></> : null}
                                {recipientCenterAddress.addressLine3 != '' ? <>{recipientCenterAddress.addressLine3}<br /></> : null}
                                {recipientCenterAddress.city}, {recipientCenterAddress.stateAbbreviation} {recipientCenterAddress.postalCode}
                            </td>
                        </tr>
                        <tr>
                            <th>Donor Coordinator</th>
                            <td>
                                {donorCoordinator.fullName}<br />

                                {donorCoordinator.phoneNumbers.filter(x => x.phoneTypeId == PhoneType.Mobile).map((ph, index) =>
                                    <>
                                        Mobile: {ph.number}<br />
                                    </>
                                )}

                                {donorCoordinator.phoneNumbers.filter(x => x.phoneTypeId == PhoneType.Office).map((ph, index) =>
                                    <>
                                        {index > 0 ? <br /> : null}
                                        Office: {ph.number}
                                    </>
                                )}

                                <br />

                                Email: {donorCoordinator.email} 

                            </td>
                            <th>Recipient Coordinator</th>
                            <td>
                                {recipientCoordinator != null && (
                                    <>
                                        {recipientCoordinator.fullName}<br />

                                        {recipientCoordinator.phoneNumbers.filter(x => x.phoneTypeId == PhoneType.Mobile).map((ph, index) =>
                                            <>
                                                Mobile: {ph.number}<br />
                                            </>
                                        )}

                                        {recipientCoordinator.phoneNumbers.filter(x => x.phoneTypeId == PhoneType.Office).map((ph, index) =>
                                            <>
                                                {index > 0 ? <br /> : null}
                                                Office: {ph.number}
                                            </>
                                        )}

                                        <br />

                                        Email: {recipientCoordinator.email} 
                                    </>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <th>Donor Alias</th>
                            <td>{donorAlias} (UNOS ID: {donorUnosId})</td>
                            <th>Recipient Alias</th>
                            <td>{recipientAlias}</td>
                        </tr>
                        <tr>
                            <th>Donor Surgeon</th>
                            <td>
                                {donorSurgeon != null ?
                                    <>
                                        {donorSurgeon.fullName}<br />

                                        {donorSurgeon.phoneNumbers.filter(x => x.phoneTypeId == PhoneType.Mobile).map((ph, index) =>
                                            <>
                                                Mobile: {ph.number}<br />
                                            </>
                                        )}

                                        {donorSurgeon.phoneNumbers.filter(x => x.phoneTypeId == PhoneType.Office).map((ph, index) =>
                                            <>
                                                {index > 0 ? <br /> : null}
                                                Office: {ph.number}
                                            </>
                                        )}
                                    </>
                                    :
                                    <>Not Entered</>
                                }
                            </td>
                            <th>Recipient Surgeon</th>
                            <td>
                                {recipientSurgeon ?
                                    <>
                                        {recipientSurgeon.fullName}<br />

                                        {recipientSurgeon.phoneNumbers.filter(x => x.phoneTypeId == PhoneType.Mobile).map((ph, index) =>
                                            <>
                                                Mobile: {ph.number}<br />
                                            </>
                                        )}

                                        {recipientSurgeon.phoneNumbers.filter(x => x.phoneTypeId == PhoneType.Office).map((ph, index) =>
                                            <>
                                                {index > 0 ? <br /> : null}
                                                Office: {ph.number}
                                            </>
                                        )}
                                    </>
                                    :
                                    <>Not Entered</>
                                }
                            </td>
                        </tr>
                        <tr>
                            <th>GPS Number</th>
                            <td>{gpsNumber ? gpsNumber : '[pending]'}</td>
                            <th>GPS Link</th>
                            <td>{gpsLink ? <a href={gpsLink} target='_blank'>Click Here</a> : '[pending]'}</td>
                        </tr>
                        <tr>
                            <th colSpan={2} style={{ fontSize: 20, textAlign: 'center', backgroundColor: 'lightgrey' }}>Primary Transportation Plan</th>
                            <th colSpan={2} style={{ fontSize: 20, textAlign: 'center', backgroundColor: 'lightgrey' }}>Secondary Transportation Plan</th>
                        </tr>
                        <tr>
                            <th>Scheduled Pickup</th>
                            <td>{kidneyPickupDateTime ? kidneyPickupDateTime : 'Not Entered'} {kidneyPickupDateTime ? donorCenterTz : ''}</td>
                            <th>Scheduled Pickup</th>
                            <td>{kidneyPickupDateTimeBackup ? kidneyPickupDateTimeBackup : 'Not Entered'} {kidneyPickupDateTimeBackup ? donorCenterTz : ''}</td>
                        </tr>
                        <tr>
                            <th>Scheduled Delivery</th>
                            <td>{kidneyArrivalDateTime ? kidneyArrivalDateTime : 'Not Entered'} {kidneyArrivalDateTime ? recipientCenterTimezone : ''}</td>
                            <th>Scheduled Delivery</th>
                            <td>{kidneyArrivalDateTimeBackup ? kidneyArrivalDateTimeBackup : 'Not Entered'} {kidneyArrivalDateTimeBackup ? recipientCenterTimezone : ''}</td>
                        </tr>
                        <tr>
                            <th>Transportation Method</th>
                            <td>{transportMethod ? TransportMethod[transportMethod] : '[pending]'}</td>
                            <th>Transportation Method</th>
                            <td>{transportMethodBackup ? TransportMethod[transportMethodBackup] : '[pending]'}</td>
                        </tr>

                        {max > 0 && (
                            <>
                                {rows.map((r, i) => {
                                    let ph = arrays.primaryHeaders[i];
                                    let pv = arrays.primaryValues[i];
                                    let bh = arrays.backupHeaders[i];
                                    let bv = arrays.backupValues[i];

                                    return (
                                        <tr key={'r-' + i}>
                                            <th>{ph ? ph : null}</th>
                                            <td>{pv ? pv : null}</td>
                                            <th>{bh ? bh : null}</th>
                                            <td>{bv ? bv : null}</td>
                                        </tr>
                                    );
                                })}
                            </>
                        )}
                        <tr>
                            <th colSpan={4} style={{ fontSize: 20, textAlign: 'center', backgroundColor: 'lightgrey' }}>Tubes to pack with the kidney</th>
                        </tr>
                        <tr>
                            <td colSpan={4}>
                                <ul>
                                    {tubes.map((tube, index) =>
                                        <li key={'tube-' + index}>{tube}</li>
                                    )}
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <th colSpan={4} style={{ fontSize: 20, textAlign: 'center', backgroundColor: 'lightgrey' }}>Donor kidney laterality</th>
                        </tr>
                        <tr>
                            <td colSpan={4}>
                                <ul>
                                    <li><strong>{whichKidney}</strong></li>
                                </ul>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        actionCenter: appState.actionCenter,
        transportTypeList: appState.swaps.transportTypeList
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getLogisticsData: (donorId, recipientId) => {
            return dispatch(SwapActionCreators.getLogisticsDataByDonorRecipient(donorId, recipientId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(LogisticsPlan);